import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { postRequest } from 'apis/ApiInstence';
import axiosInstance from 'apis/axiosConfig';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';

const sanitizeDecimalValue = (value) => {
  if (value === null || value === undefined) return '';
  const normalized = String(value).trim().replace(/[^0-9.]/g, '');
  const parts = normalized.split('.');

  if (parts.length <= 1) return normalized;

  return `${parts[0]}.${parts.slice(1).join('')}`;
};

const AddProduct = () => {
  const [formData, setFormData] = useState({
    name: '',
    cost_price: '',
    selling_price: '',
    offer: '',
    description: '',
    images: null,
    stock_quantity: '',
    dimensions: '',
    category: '',
    subcategory: '',
    age_group: '',
    weight: '',
    status: 'active'
  });
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [categoryRes, subcategoryRes] = await Promise.all([
          axiosInstance.get('/categories'),
          axiosInstance.get('/subcategories'),
        ]);
        setCategories(Array.isArray(categoryRes.data) ? categoryRes.data : []);
        setSubcategories(Array.isArray(subcategoryRes.data) ? subcategoryRes.data : []);
      } catch (error) {
        console.error('Failed to load category options:', error);
      }
    };

    fetchOptions();
  }, []);

  const filteredSubcategories = useMemo(() => {
    return subcategories.filter((item) => item.parent === formData.category);
  }, [subcategories, formData.category]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      ...(name === 'category' ? { subcategory: '' } : {}),
      [name]: type === 'file' ? files : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const sanitizedWeight = sanitizeDecimalValue(formData.weight);

    if (formData.weight && !sanitizedWeight) {
      toast.error('Weight must be a numeric value, for example 2 or 2.5');
      return;
    }

    const formDataToSend = new FormData();
    for (const key in formData) {
      if (key === 'images' && formData.images) {
        for (let i = 0; i < formData.images.length; i++) {
          formDataToSend.append('images', formData.images[i]);
        }
      } else if (key === 'weight') {
        formDataToSend.append(key, sanitizedWeight);
      } else {
        formDataToSend.append(key, formData[key]);
      }
    }

    try {
      await postRequest('products', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Product added successfully!', {
        position: 'top-right',
        autoClose: 2000
      });
      setTimeout(() => {
        navigate('/products');
      }, 2000);
    } catch (err) {
      console.error('Failed to add product:', err);
      toast.error('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4">
        <h4 className="text-white font-weight-bold">Add Product</h4>
      </div>
      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="row mx-4 mt-5">
          <div className="col-sm-4 mt-2">
            <label htmlFor="toy_name" className="admin-label">
              Toy Name
            </label>
            <input id="toy_name" type="text" name="name" value={formData.name} onChange={handleChange} className="form-control" required />
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="cost_price" className="admin-label">
              Cost Price
            </label>
            <input
              id="cost_price"
              type="text"
              name="cost_price"
              value={formData.cost_price}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="selling_price" className="admin-label">
              Selling Price
            </label>
            <input
              id="selling_price"
              type="text"
              name="selling_price"
              value={formData.selling_price}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="offer" className="admin-label">
              Offer (%)
            </label>
            <div className="input-group">
              <input
                id="offer"
                type="text"
                name="offer"
                value={formData.offer}
                onChange={handleChange}
                className="form-control"
                placeholder="Enter offer like 20 or 20%"
              />
              <div className="input-group-append">
                <span className="input-group-text">%</span>
              </div>
            </div>
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="stock_quantity" className="admin-label">
              Stock Quantity
            </label>
            <input
              id="stock_quantity"
              type="text"
              name="stock_quantity"
              value={formData.stock_quantity}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="dimensions" className="admin-label">
              Dimensions
            </label>
            <input
              id="dimensions"
              type="text"
              name="dimensions"
              value={formData.dimensions}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="weight" className="admin-label">
              Weight (kg)
            </label>
            <input
              id="weight"
              type="text"
              name="weight"
              value={formData.weight}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter weight like 2 or 2kg"
            />
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="category" className="admin-label">
              Category
            </label>
            <select id="category" name="category" value={formData.category} onChange={handleChange} className="form-control" required>
              <option value="">-- Select Category --</option>
              {categories.map((item) => (
                <option key={item.id} value={item.name}>{item.name}</option>
              ))}
            </select>
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="subcategory" className="admin-label">
              Subcategory
            </label>
            <select
              id="subcategory"
              name="subcategory"
              value={formData.subcategory}
              onChange={handleChange}
              className="form-control"
              disabled={!formData.category}
            >
              <option value="">-- Select Subcategory --</option>
              {filteredSubcategories.map((item) => (
                <option key={item.id} value={item.name}>{item.name}</option>
              ))}
            </select>
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="age_group" className="admin-label">
              Age Group
            </label>
            <select id="age_group" name="age_group" value={formData.age_group} onChange={handleChange} className="form-control" required>
              <option value="">-- Select Age Group --</option>
              <option value="0-1">0-1</option>
              <option value="1-2">1-2</option>
              <option value="2-3">2-3</option>
              <option value="3-4">3-4</option>
              <option value="4+">4+</option>
            </select>
          </div>

          <div className="col-sm-12 mt-4">
            <label htmlFor="description" className="admin-label">
              Description (Markdown)
            </label>
            <textarea
              id="description"
              rows={6}
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="form-control"
              placeholder="Use **bold**, headings, - lists, etc."
            ></textarea>

            <div className="mt-3">
              <h5 className="admin-label">Preview:</h5>
              <div className="p-3 border rounded bg-light">
                <ReactMarkdown>{formData.description}</ReactMarkdown>
              </div>
            </div>
          </div>

          <div className="col-sm-12 mt-4">
            <label htmlFor="images" className="admin-label">
              Image
            </label>
            <input id="images" type="file" name="images" className="form-control" onChange={handleChange} multiple />

            {formData.images && formData.images.length > 0 && (
              <div className="mt-3">
                <h6>Image Preview:</h6>
                <div className="d-flex flex-wrap">
                  {Array.from(formData.images).map((file, index) => (
                    <img key={index} src={URL.createObjectURL(file)} alt={`Preview ${index}`} width={100} className="mr-2 mb-2 border" />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 text-right mx-4">
          <button type="submit" className="btn btn-md addbtn">
            Add Product
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddProduct;
